import React, { useState } from 'react';
import * as ContextMenu from '@radix-ui/react-context-menu';
import { ExternalLink, Edit, Users, Home, Layers, CheckCircle, Play, Square, Info, Zap, Copy, Truck, PlayIcon, Pause, PanelTop, Clock, Factory, RotateCcw } from 'lucide-react';
import { BrickWall, Grid2X2 } from "lucide-react";
import { hasPermission } from '../../../lib/permissions';
import { useAuth } from '../../../contexts/AuthContext';
import { checkJobExists, duplicateJob } from '../../../lib/supabase/duplicate-job';
import Toast from '../../common/Toast';

// import "./ContextMenu.css";

const AssignmentContextMenu = ({
    children,
    assignment,
    onOpenAssignment,
    onEditAssignment,
    onAssignDesigner,
    onStartProduction,
    onEndProduction,
    onForceFinish,
    onDuplicateJob,
    onToggleHold,
    onToggleQuoteHold,
    onToggleLayoutOnly,
    onToggleWaiting,
    onToggleSentProduction,
    onUndoDeliveryCompletion,
    availableDesigners = [],
    page = 'editor',
    userRole = null,
    companyId = null,
    // When true, renders only the header + Open Assignment + Edit Assignment
    // items — nothing else (no Duplicate Job, Pause/Resume, Force Finish,
    // etc). Additive-only: existing callers that don't pass this prop are
    // completely unaffected.
    minimal = false,
}) => {

    let isSeparated = assignment._quoteType !== null && assignment._quoteType !== undefined;
    let separatedQuoteType = assignment._quoteType;

    if (page === "delivery") {
        isSeparated = true;
        separatedQuoteType = assignment?.quote_info?.quote_type
    }


    const { userProfile, company } = useAuth();

    const isCancelled = !!(assignment?.is_cancelled || assignment?.isCancelled);

    const CancelledMenuItem = ({ children }) => (
        <ContextMenu.Item
            className="context-menu-item opacity-50 cursor-not-allowed text-gray-400"
            disabled
        >
            {children}
            <span className="ml-auto text-[10px] text-red-500 font-semibold">
                Job is cancelled
            </span>
        </ContextMenu.Item>
    );

    // Duplicate Job Modal State
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
    const [newJobId, setNewJobId] = useState('');
    const [duplicateError, setDuplicateError] = useState('');
    const [isDuplicating, setIsDuplicating] = useState(false);

    // Handle duplicate job submission
    const handleDuplicateSubmit = async () => {
        if (!newJobId.trim()) {
            setDuplicateError('Job ID is required');
            return;
        }

        setIsDuplicating(true);
        setDuplicateError('');

        try {
            // Check if job exists
            const exists = await checkJobExists(newJobId.trim(), company?.id);

            if (exists) {
                setDuplicateError('A job with this ID already exists');
                setIsDuplicating(false);
                return;
            }

            // Duplicate the job
            await duplicateJob(
                assignment._originalId || assignment._id,
                newJobId.trim(),
                userProfile?.id,
                userProfile?.full_name || userProfile?.username
            );

            // Close modal and reset state
            setIsDuplicateModalOpen(false);
            setNewJobId('');
            setDuplicateError('');

            // Show success toast
            Toast.success(`Job successfully duplicated as ${newJobId.trim()}`);

            // Call parent callback if provided to refresh the table
            if (onDuplicateJob) {
                onDuplicateJob();
            }
        } catch (error) {
            console.error('Error duplicating job:', error);
            setDuplicateError(error.message || 'Failed to duplicate job');
            Toast.error(error.message || 'Failed to duplicate job');
        } finally {
            setIsDuplicating(false);
        }
    };

    const hasQuoteType = (type) => {
        return assignment.quotes?.[type]?.isRequired || assignment?.job_quotes?.[type]?.is_required;
    };

    const getCurrentDesigner = (quoteType) => {
        return assignment.quotes?.[quoteType]?.designers?.[0];
    };

    const isDesignCompleted = (quoteType) => {
        return assignment.quotes?.[quoteType]?.designStatus === 'completed';
    };

    const buildDeliveryForceFinishPayload = () => {
        // Delivery page already has the correct shape
        if (assignment?.quote_info) {
            return assignment;
        }

        // Editor page accordion child row
        if (assignment?._isAccordionChild && assignment?._quoteData) {
            return {
                ...assignment,
                quote_info: {
                    quote_type: assignment?._quoteData?.quote_type || assignment?._quoteType || null,
                    quoteId: assignment?._quoteData?.id || null,
                    child_id: assignment?._quoteData?.id || null,
                    childUniqueId:
                        assignment?._quoteData?.child_unique_id ||
                        assignment?._quoteData?.child_uniqueId ||
                        null,
                    delivery_count: assignment?._quoteData?.delivery_count ?? 0,
                },
                customer_name: assignment?.customerName || assignment?.customer_name || null,
                customer_address: assignment?.customerAddress || assignment?.customer_address || null,
                customer_phone: assignment?.customerNumber || assignment?.customer_number || null,
                company_id: assignment?.company_id || companyId || company?.id || null,
            };
        }

        // Parent/combined rows should not force-finish delivery
        return null;
    };

    const isDeliveryFinished = () => {
        const deliveryAssignment = buildDeliveryForceFinishPayload();
        if (!deliveryAssignment) return false;

        const qt = deliveryAssignment?.quote_info?.quote_type;
        const qId = deliveryAssignment?.quote_info?.quoteId || deliveryAssignment?.quote_info?.child_id;
        const deliveryCount = deliveryAssignment?.quote_info?.delivery_count ?? 0;

        const deliveries = deliveryAssignment?.job_deliveries || assignment?.job_deliveries || [];

        const delivery = deliveries.find(d =>
            qId && d.child_id
                ? d.child_id === qId
                : d.quote_type === qt && (d.delivery_count ?? 0) === deliveryCount
        );

        return delivery?.delivery_status === 'delivered' || delivery?.completed === true;
    };

    // The delivery row this card displays — same matching as the delivery page's
    // card status (DeliveryQuoteSchedule deliveryRecord), so the undo button only
    // shows when the card itself shows "Completed".
    const getCardDeliveryRecord = () => {
        const quoteId = assignment?.quote_info?.quoteId || null;
        const qt = assignment?.quote_info?.quote_type;
        return (assignment?.job_deliveries || []).find(d =>
            quoteId
                ? d.child_unique_id === quoteId
                : (d.quote_type === qt && !d.child_unique_id)
        ) || null;
    };

    const cardDeliveryRecord = page === 'delivery' ? getCardDeliveryRecord() : null;
    const canUndoDeliveryCompletion =
        !!onUndoDeliveryCompletion &&
        !!cardDeliveryRecord?.id &&
        cardDeliveryRecord.delivery_status === 'delivered' &&
        hasPermission(userProfile?.role, ["super_admin", "admin"]);

    const deliveryForceFinishPayload = buildDeliveryForceFinishPayload();
    const canForceFinishDelivery = !!deliveryForceFinishPayload;
    const deliveryAlreadyFinished = isDeliveryFinished();

    const getProductionStatus = () => {
        // For separated view, check specific quote type
        if (isSeparated) {
            const production = assignment?.quotes?.[separatedQuoteType]?.production; // CHANGED
            if (!production || !production.phase) return "not_started";
            return production.phase;
        }

        // For combined view, return most advanced phase
        const requiredQuotes = ['roof', 'wall', 'floor'].filter(type =>
            assignment.quotes?.[type]?.isRequired
        );

        const phases = requiredQuotes.map(type =>
            assignment.quotes?.[type]?.production?.phase || 'not_started' // CHANGED
        );

        if (phases.includes('completed')) return 'completed';
        if (phases.includes('in_production')) return 'in_production';
        if (phases.includes('cutting_ended')) return 'cutting_ended';
        if (phases.includes('in_cutting')) return 'in_cutting';
        return 'not_started';
    };

    const getQuoteIcon = (quoteType) => {
        switch (quoteType) {
            case 'roof': return <Home size={14} className="mr-2" />;
            case 'wall': return <BrickWall size={14} className="mr-2" />;
            case 'floor': return <Grid2X2 size={14} className="mr-2" />;
            default: return <Layers size={14} className="mr-2" />;
        }
    };

    const renderAssigneeInfo = () => (
        <div className="p-3 max-w-xs">
            <h4 className="font-semibold text-sm mb-3 text-gray-900">Assignment Information</h4>

            {/* Design Quotes */}
            <div className="space-y-2 mb-3">
                <h5 className="text-xs font-medium text-gray-700">Design:</h5>
                {['roof', 'wall', 'floor'].map(quoteType => {
                    if (!hasQuoteType(quoteType)) return null;
                    const designer = getCurrentDesigner(quoteType);
                    const isCompleted = isDesignCompleted(quoteType);

                    return (
                        <div key={quoteType} className="flex items-center justify-between text-xs">
                            <div className="flex items-center gap-2">
                                {quoteType === 'roof' && <Home size={12} />}
                                {quoteType === 'wall' && <BrickWall size={12} />}
                                {quoteType === 'floor' && <Grid2X2 size={12} />}
                                <span className="capitalize font-medium">{quoteType}:</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <span className="text-gray-600">
                                    {designer ? designer.name : 'Unassigned'}
                                </span>
                                {isCompleted && <CheckCircle size={12} className="text-green-500" />}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Estimation */}
            {(assignment.jobType === 'Estimation' || assignment.jobType === 'Design & Estimation') && (
                <div className="space-y-2 mb-3">
                    <h5 className="text-xs font-medium text-gray-700">Estimation:</h5>
                    <div className="flex items-center justify-between text-xs">
                        <span className="font-medium">Estimator:</span>
                        <div className="flex items-center gap-1">
                            <span className="text-gray-600">
                                {assignment.estimator?.name || 'Unassigned'}
                            </span>
                            {(assignment.estimator?.isCompleted || assignment.estimator?.is_completed) && <CheckCircle size={12} className="text-green-500" />}
                        </div>
                    </div>
                </div>
            )}

            {/* Production */}
            {assignment.production && assignment.production.userId && (
                <div className="border-t pt-2">
                    <h5 className="text-xs font-medium text-gray-700 mb-1">Production:</h5>
                    <div className="flex items-center justify-between text-xs">
                        <span className="font-medium">Lead:</span>
                        <span className="text-gray-600">{assignment.production.name}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs mt-1">
                        <span className="font-medium">Phase:</span>
                        <span className={`px-1 py-0.5 rounded text-xs ${assignment.production.phase === 'completed' ? 'bg-green-100 text-green-700' :
                            assignment.production.phase === 'in_production' ? 'bg-blue-100 text-blue-700' :
                                assignment.production.phase === 'cutting_ended' ? 'bg-purple-100 text-purple-700' :
                                    assignment.production.phase === 'in_cutting' ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-gray-100 text-gray-700'
                            }`}>
                            {assignment.production.phase === 'in_cutting' ? 'In Cutting' :
                                assignment.production.phase === 'cutting_ended' ? 'Cutting Ended' :
                                    assignment.production.phase === 'in_production' ? 'In Production' :
                                        assignment.production.phase === 'completed' ? 'Completed' :
                                            'Not Started'}
                        </span>
                    </div>
                    {assignment.production.startCuttingAt && (
                        <div className="flex items-center justify-between text-xs mt-1">
                            <span className="font-medium">Cutting Started:</span>
                            <span className="text-gray-600">
                                {new Date(assignment.production.startCuttingAt).toLocaleDateString('en-AU')}
                            </span>
                        </div>
                    )}
                    {assignment.production.startProductionAt && (
                        <div className="flex items-center justify-between text-xs mt-1">
                            <span className="font-medium">Production Started:</span>
                            <span className="text-gray-600">
                                {new Date(assignment.production.startProductionAt).toLocaleDateString('en-AU')}
                            </span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    const renderDesignerSubmenu = (quoteType, icon) => {
        const isCompleted = isDesignCompleted(quoteType);
        const currentDesigner = getCurrentDesigner(quoteType);

        if (isCompleted && currentDesigner) {
            // Show completed designer info (non-interactive)
            return (
                <ContextMenu.Sub key={quoteType}>
                    <ContextMenu.SubTrigger className="context-menu-item context-menu-subtrigger">
                        {icon}
                        <span className="capitalize">{quoteType}</span>
                        <CheckCircle size={14} className="ml-auto text-green-500" />
                    </ContextMenu.SubTrigger>
                    <ContextMenu.Portal>
                        <ContextMenu.SubContent className="context-menu-content context-menu-subcontent">
                            <div className="p-3 text-center">
                                <CheckCircle size={24} className="mx-auto text-green-500 mb-2" />
                                <div className="font-semibold text-sm text-gray-900">
                                    {currentDesigner.name}
                                </div>
                                <div className="text-xs text-green-600 font-medium mt-1">
                                    Design Completed
                                </div>
                            </div>
                        </ContextMenu.SubContent>
                    </ContextMenu.Portal>
                </ContextMenu.Sub>
            );
        }

        // Show normal assignment menu for incomplete designs
        return (
            <ContextMenu.Sub key={quoteType}>
                <ContextMenu.SubTrigger className="context-menu-item context-menu-subtrigger">
                    {icon}
                    <span className="capitalize">{quoteType}</span>
                    <div className="context-menu-arrow">▶</div>
                </ContextMenu.SubTrigger>
                <ContextMenu.Portal>
                    <ContextMenu.SubContent className="context-menu-content context-menu-subcontent">
                        {availableDesigners.length > 0 ? (
                            availableDesigners.map((designer) => {
                                const isCurrent = currentDesigner?.userId === designer.userId;
                                return (
                                    <ContextMenu.Item
                                        key={designer.userId}
                                        className={`context-menu-item ${isCurrent ? 'context-menu-current' : ''}`}
                                        onSelect={() => onAssignDesigner(assignment._originalId || assignment._id, quoteType, designer.userId, designer.username, assignment)}
                                    >
                                        <span>{designer.username}</span>
                                        {isCurrent && <span className="context-menu-badge">Current</span>}
                                    </ContextMenu.Item>
                                );
                            })
                        ) : (
                            <ContextMenu.Item className="context-menu-item" disabled>
                                No designers available
                            </ContextMenu.Item>
                        )}
                    </ContextMenu.SubContent>
                </ContextMenu.Portal>
            </ContextMenu.Sub>
        );
    };

    // Render direct designer list for separated view
    const renderDirectDesignerList = () => {
        const isCompleted = isDesignCompleted(separatedQuoteType);
        const currentDesigner = getCurrentDesigner(separatedQuoteType);

        if (isCompleted && currentDesigner) {
            // Show completed status - non-interactive
            return (
                <div className="p-3 text-center">
                    <CheckCircle size={24} className="mx-auto text-green-500 mb-2" />
                    <div className="font-semibold text-sm text-gray-900">
                        {currentDesigner.name}
                    </div>
                    <div className="text-xs text-green-600 font-medium mt-1">
                        Design Completed
                    </div>
                </div>
            );
        }

        // Show assignable designers
        return (
            <>
                {availableDesigners.length > 0 ? (
                    availableDesigners.map((designer) => {
                        const isCurrent = currentDesigner?.userId === designer.userId;
                        return (
                            <ContextMenu.Item
                                key={designer.userId}
                                className={`context-menu-item ${isCurrent ? 'context-menu-current' : ''}`}
                                onSelect={() => onAssignDesigner(assignment._originalId || assignment._id, separatedQuoteType, designer.userId, designer.username)}
                            >
                                <span>{designer.username}</span>
                                {isCurrent && <span className="context-menu-badge">Current</span>}
                            </ContextMenu.Item>
                        );
                    })
                ) : (
                    <ContextMenu.Item className="context-menu-item" disabled>
                        No designers available
                    </ContextMenu.Item>
                )}
            </>
        );
    };

    // Render production action with quote type selection for combined view
    const renderProductionQuoteSubmenu = (action, actionLabel, icon) => {
        const requiredQuotes = ['roof', 'wall', 'floor'].filter(type => hasQuoteType(type));

        if (requiredQuotes.length === 0) return null;

        return (
            <ContextMenu.Sub>
                <ContextMenu.SubTrigger className="context-menu-item context-menu-subtrigger">
                    {icon}
                    {actionLabel}
                    <div className="context-menu-arrow">▶</div>
                </ContextMenu.SubTrigger>
                <ContextMenu.Portal>
                    <ContextMenu.SubContent className="context-menu-content context-menu-subcontent">
                        {requiredQuotes.map(quoteType => (
                            <ContextMenu.Item
                                key={quoteType}
                                className="context-menu-item"
                                onSelect={() => {
                                    const assignmentId = assignment._productionRowId || assignment._originalId || assignment._id;

                                    if (action.includes('start')) {
                                        onStartProduction && onStartProduction(assignmentId, action, quoteType);
                                    } else {
                                        onEndProduction && onEndProduction(assignmentId, action, quoteType);
                                    }
                                }}
                            >
                                {getQuoteIcon(quoteType)}
                                <span className="capitalize">{quoteType}</span>
                            </ContextMenu.Item>
                        ))}
                    </ContextMenu.SubContent>
                </ContextMenu.Portal>
            </ContextMenu.Sub>
        );
    };

    if (page === "production" && !hasPermission(userProfile?.role, ["app_owner", "super_admin", "admin"]))
        return (
            children
        );

    if (minimal) {
        return (
            <ContextMenu.Root>
                <ContextMenu.Trigger asChild>
                    {children}
                </ContextMenu.Trigger>

                <ContextMenu.Portal>
                    <ContextMenu.Content className="context-menu-content">
                        <div className="context-menu-header">
                            <div className="font-semibold text-gray-900">
                                {assignment.uniqueId || assignment.unique_id}
                            </div>
                            <div className="text-xs text-gray-500">ID: {assignment._originalId || assignment._id || assignment.id}</div>
                        </div>

                        <ContextMenu.Separator className="context-menu-separator" />

                        {onOpenAssignment && (
                            <ContextMenu.Item
                                className="context-menu-item"
                                onSelect={() => onOpenAssignment(assignment._originalId || assignment._id || assignment.id)}
                            >
                                <ExternalLink size={14} className="mr-2" />
                                Open Assignment
                            </ContextMenu.Item>
                        )}

                        {onEditAssignment && (
                            isCancelled ? (
                                <CancelledMenuItem>
                                    <Edit size={14} className="mr-2" />
                                    Edit Assignment
                                </CancelledMenuItem>
                            ) : (
                                <ContextMenu.Item
                                    className="context-menu-item"
                                    onSelect={() => onEditAssignment(assignment._originalId || assignment._id || assignment.id)}
                                >
                                    <Edit size={14} className="mr-2" />
                                    Edit Assignment
                                </ContextMenu.Item>
                            )
                        )}
                    </ContextMenu.Content>
                </ContextMenu.Portal>
            </ContextMenu.Root>
        );
    }

    return (
        <ContextMenu.Root>
            <ContextMenu.Trigger asChild>
                {children}
            </ContextMenu.Trigger>

            <ContextMenu.Portal>
                <ContextMenu.Content className="context-menu-content">
                    {/* Job Info Header - Show quote type if separated */}
                    <div className="context-menu-header">
                        <div className="font-semibold text-gray-900">
                            {assignment.uniqueId || assignment.unique_id}
                            {isSeparated && (
                                <span className="ml-2 text-xs font-medium text-blue-600 capitalize">
                                    ({separatedQuoteType})
                                </span>
                            )}
                        </div>
                        <div className="text-xs text-gray-500">ID: {assignment._originalId || assignment._id || assignment.id}</div>
                    </div>

                    <ContextMenu.Separator className="context-menu-separator" />

                    {/* Open Assignment */}
                    {onOpenAssignment && (
                        <ContextMenu.Item
                            className="context-menu-item"
                            onSelect={() => onOpenAssignment(assignment._originalId || assignment._id || assignment.id)}
                        >
                            <ExternalLink size={14} className="mr-2" />
                            Open Assignment
                        </ContextMenu.Item>
                    )}

                    {/* Edit Assignment */}
                    {onEditAssignment && (
                        isCancelled ? (
                            <CancelledMenuItem>
                                <Edit size={14} className="mr-2" />
                                Edit Assignment
                            </CancelledMenuItem>
                        ) : (
                            <ContextMenu.Item
                                className="context-menu-item"
                                onSelect={() => onEditAssignment(assignment._originalId || assignment._id || assignment.id)}
                            >
                                <Edit size={14} className="mr-2" />
                                Edit Assignment
                            </ContextMenu.Item>
                        )
                    )}

                    {/* Duplicate Job */}
                    {hasPermission(userProfile?.role, ["admin", "super_admin", "designer"]) && page !== "delivery" && page !== "production" &&
                        isCancelled ? (
                        <CancelledMenuItem>
                            <Edit size={14} className="mr-2" />
                            Duplicate Job
                        </CancelledMenuItem>
                    ) : (
                        <ContextMenu.Item
                            className="context-menu-item"
                            onSelect={() => setIsDuplicateModalOpen(true)}
                        >
                            <Copy size={14} className="mr-2" />
                            Duplicate Job
                        </ContextMenu.Item>
                    )
                    }

                    {page === "department" ?
                        isCancelled ? (
                            <CancelledMenuItem>
                                <Edit size={14} className="mr-2" />
                                Pause / Play
                            </CancelledMenuItem>
                        ) : (

                            <ContextMenu.Sub>
                                <ContextMenu.SubTrigger className="context-menu-item context-menu-subtrigger">
                                    <Pause size={14} className="mr-2" />
                                    Pause / Resume
                                    <div className="context-menu-arrow">▶</div>
                                </ContextMenu.SubTrigger>
                                <ContextMenu.Portal>
                                    <ContextMenu.SubContent className="context-menu-content context-menu-subcontent">
                                        {/* Estimation — uses job-level on_hold */}
                                        {/* {(assignment.job_type === 'Estimation' || assignment.job_type === 'Design & Estimation') && (
                                        <ContextMenu.Item
                                            className="context-menu-item"
                                            onSelect={() => onToggleHold && onToggleHold(assignment._originalId || assignment._id || assignment.id, assignment?.on_hold)}
                                        >
                                            {assignment?.on_hold ? <PlayIcon size={14} className="mr-2" /> : <Pause size={14} className="mr-2" />}
                                            {assignment?.on_hold ? "Resume Estimation" : "Pause Estimation"}
                                        </ContextMenu.Item>
                                    )} */}


                                        <ContextMenu.Item
                                            key={assignment.quote_info?.quote_type}
                                            className="context-menu-item"
                                            onSelect={() => onToggleQuoteHold && onToggleQuoteHold(assignment._originalId || assignment._id || assignment.id, assignment?.quote_info?.quote_type, assignment.quote_info?.on_hold)}
                                        >
                                            {assignment.quote_info?.on_hold ? <PlayIcon size={14} className="mr-2" /> : <Pause size={14} className="mr-2" />}
                                            <span className="capitalize">{assignment.quote_info?.on_hold ? `Resume ${assignment?.quote_info?.quote_type}` : `Pause ${assignment?.quote_info?.quote_type}`}</span>
                                        </ContextMenu.Item>

                                    </ContextMenu.SubContent>
                                </ContextMenu.Portal>
                            </ContextMenu.Sub>
                        )
                        : page !== "production" &&
                            isCancelled ? (
                            <CancelledMenuItem>
                                <Edit size={14} className="mr-2" />
                                Pause / Play
                            </CancelledMenuItem>
                        ) : (
                            <ContextMenu.Sub>
                                <ContextMenu.SubTrigger className="context-menu-item context-menu-subtrigger">
                                    <Pause size={14} className="mr-2" />
                                    Pause / Resume
                                    <div className="context-menu-arrow">▶</div>
                                </ContextMenu.SubTrigger>
                                <ContextMenu.Portal>
                                    <ContextMenu.SubContent className="context-menu-content context-menu-subcontent">
                                        {/* Estimation — uses job-level on_hold */}
                                        {(assignment.jobType === 'Estimation' || assignment.jobType === 'Design & Estimation' || assignment.job_type === "Estimation" || assignment.job_type === "Design & Estimation") && (
                                            <ContextMenu.Item
                                                className="context-menu-item"
                                                onSelect={() => onToggleHold && onToggleHold(assignment._originalId || assignment._id || assignment.id, assignment?.on_hold)}
                                            >
                                                {assignment?.on_hold ? <PlayIcon size={14} className="mr-2" /> : <Pause size={14} className="mr-2" />}
                                                {assignment?.on_hold ? "Resume Estimation" : "Pause Estimation"}
                                            </ContextMenu.Item>
                                        )}

                                        {/* Quote types — uses per-quote on_hold */}
                                        {['roof', 'wall', 'floor', 'timber'].map(quoteType => {
                                            if (!hasQuoteType(quoteType)) return null;
                                            const isOnHold = assignment.quotes?.[quoteType]?.on_hold
                                            return (
                                                <ContextMenu.Item
                                                    key={quoteType}
                                                    className="context-menu-item"
                                                    onSelect={() => onToggleQuoteHold && onToggleQuoteHold(assignment._originalId || assignment._id || assignment.id, quoteType, isOnHold)}
                                                >
                                                    {isOnHold ? <PlayIcon size={14} className="mr-2" /> : <Pause size={14} className="mr-2" />}
                                                    <span className="capitalize">{isOnHold ? `Resume ${quoteType}` : `Pause ${quoteType}`}</span>
                                                </ContextMenu.Item>
                                            );
                                        })}
                                    </ContextMenu.SubContent>
                                </ContextMenu.Portal>
                            </ContextMenu.Sub>
                        )
                    }




                    {/* Layout Only toggle — available on department/editor pages for admin+ */}
                    {onToggleLayoutOnly && page !== 'production' && page !== 'delivery' &&
                        hasPermission(userProfile?.role, ["app_owner", "super_admin", "admin", "designer"]) && (
                            isCancelled ? (
                                <CancelledMenuItem>
                                    <Edit size={14} className="mr-2" />
                                    {assignment?.quote_info?.layout_only ? 'Remove Layout Only' : 'Mark as Layout Only'}
                                </CancelledMenuItem>
                            ) : (
                                <>

                                    <ContextMenu.Separator className="context-menu-separator" />
                                    <ContextMenu.Item
                                        className="context-menu-item"
                                        onSelect={() => onToggleLayoutOnly(
                                            assignment._originalId || assignment._id || assignment.id,
                                            assignment?.quote_info?.quote_type || assignment?._quoteType,
                                            assignment?.quote_info?.layout_only,
                                            assignment?.quote_info?.id,
                                            assignment.uniqueId || assignment.unique_id
                                        )}
                                    >
                                        <PanelTop size={14} className="mr-2" />
                                        {assignment?.quote_info?.layout_only ? 'Remove Layout Only' : 'Mark as Layout Only'}
                                    </ContextMenu.Item>
                                </>
                            )
                        )}

                    {/* Waiting Job toggle — always visible, no page restriction */}
                    {onToggleWaiting && hasPermission(userProfile?.role, ["app_owner", "super_admin", "admin"]) && (
                        isCancelled ? (
                            <CancelledMenuItem>
                                <Edit size={14} className="mr-2" />
                                {assignment?.quote_info?.waiting_job ? 'Remove Waiting' : 'Mark as Waiting'}
                            </CancelledMenuItem>
                        ) : (
                            <>
                                <ContextMenu.Separator className="context-menu-separator" />
                                <ContextMenu.Item
                                    className="context-menu-item"
                                    onSelect={() => onToggleWaiting(
                                        assignment._originalId || assignment._id || assignment.id,
                                        assignment?.quote_info?.quote_type || assignment?._quoteType,
                                        assignment?.quote_info?.waiting_job,
                                        assignment?.quote_info?.id || null,
                                        assignment.uniqueId || assignment.unique_id
                                    )}
                                >
                                    <Clock size={14} className="mr-2" />
                                    {assignment?.quote_info?.waiting_job ? 'Remove Waiting' : 'Mark as Waiting'}
                                </ContextMenu.Item>
                            </>
                        )
                    )}

                    <ContextMenu.Separator className="context-menu-separator" />

                    {onToggleSentProduction && hasPermission(userProfile?.role, ["app_owner", "super_admin", "admin", "designer"]) && (
                        isCancelled ? (
                            <CancelledMenuItem>
                                <Edit size={14} className="mr-2" />
                                {assignment?.quote_info?.sent_production ? 'Remove from Production' : 'Send to Production'}
                            </CancelledMenuItem>
                        ) : (
                            <>
                                <ContextMenu.Separator className="context-menu-separator" />
                                <ContextMenu.Item
                                    className="context-menu-item"
                                    onSelect={() => onToggleSentProduction(
                                        assignment._originalId || assignment._id || assignment.id,
                                        assignment?.quote_info?.quote_type || assignment?._quoteType,
                                        assignment?.quote_info?.sent_production,
                                        assignment?.quote_info?.id || null
                                    )}
                                >
                                    <Factory size={14} className="mr-2" />
                                    {assignment?.quote_info?.sent_production ? 'Remove from Production' : 'Send to Production'}
                                </ContextMenu.Item>
                            </>
                        )
                    )}

                    {/* Page-specific content */}
                    {(page === 'production' && hasPermission(userProfile?.role, ["app_owner", "super_admin", "admin"])) ? (
                        <>
                            {/* See Assignee - Production Page */}
                            <ContextMenu.Sub>
                                <ContextMenu.SubTrigger className="context-menu-item context-menu-subtrigger">
                                    <Info size={14} className="mr-2" />
                                    See Assignee
                                    <div className="context-menu-arrow">▶</div>
                                </ContextMenu.SubTrigger>
                                <ContextMenu.Portal>
                                    <ContextMenu.SubContent className="context-menu-content context-menu-subcontent">
                                        {renderAssigneeInfo()}
                                    </ContextMenu.SubContent>
                                </ContextMenu.Portal>
                            </ContextMenu.Sub>

                            {/* Production Control */}
                            <ContextMenu.Separator className="context-menu-separator" />

                            {(() => {
                                // For combined view, show disabled message
                                if (!isSeparated) {
                                    return (
                                        <ContextMenu.Sub>
                                            <ContextMenu.SubTrigger className="context-menu-item context-menu-subtrigger">
                                                <Info size={14} className="mr-2" />
                                                Production Controls
                                                <div className="context-menu-arrow">▶</div>
                                            </ContextMenu.SubTrigger>
                                            <ContextMenu.Portal>
                                                <ContextMenu.SubContent className="context-menu-content context-menu-subcontent">
                                                    <div className="p-3 max-w-xs text-center">
                                                        <Info size={24} className="mx-auto text-blue-500 mb-2" />
                                                        <div className="font-semibold text-sm text-gray-900 mb-1">
                                                            Production Controls Disabled
                                                        </div>
                                                        <div className="text-xs text-gray-600">
                                                            Please use <span className='text-blue-600 font-semibold'>separated view</span> to control production for individual quote types
                                                        </div>
                                                    </div>
                                                </ContextMenu.SubContent>
                                            </ContextMenu.Portal>
                                        </ContextMenu.Sub>
                                    );
                                }

                                // For separated view, show normal production controls
                                const status = getProductionStatus();

                                switch (status) {
                                    case 'not_started':
                                        return (
                                            <ContextMenu.Item
                                                className="context-menu-item"
                                                onSelect={() => onStartProduction && onStartProduction(assignment._productionRowId || assignment._originalId || assignment._id, 'start_cutting', separatedQuoteType)}
                                            >
                                                <Play size={14} className="mr-2" />
                                                Start Cutting
                                            </ContextMenu.Item>
                                        );

                                    case 'in_cutting':
                                        return (
                                            <ContextMenu.Item
                                                className="context-menu-item"
                                                onSelect={() => onEndProduction && onEndProduction(assignment._productionRowId || assignment._originalId || assignment._id, 'end_cutting', separatedQuoteType)}
                                            >
                                                <Square size={14} className="mr-2" />
                                                End Cutting
                                            </ContextMenu.Item>
                                        );

                                    case 'cutting_ended':
                                        return (
                                            <ContextMenu.Item
                                                className="context-menu-item"
                                                onSelect={() => onStartProduction && onStartProduction(assignment._productionRowId || assignment._originalId || assignment._id, 'start_production', separatedQuoteType)}
                                            >
                                                <Play size={14} className="mr-2" />
                                                Start Production
                                            </ContextMenu.Item>
                                        );

                                    case 'in_production':
                                        return (
                                            <ContextMenu.Item
                                                className="context-menu-item"
                                                onSelect={() => onEndProduction && onEndProduction(assignment._productionRowId || assignment._originalId || assignment._id, 'end_production', separatedQuoteType)}
                                            >
                                                <Square size={14} className="mr-2" />
                                                End Production
                                            </ContextMenu.Item>
                                        );

                                    case 'completed':
                                        return (
                                            <ContextMenu.Item className="context-menu-item" disabled>
                                                <CheckCircle size={14} className="mr-2 text-green-500" />
                                                Production Completed
                                            </ContextMenu.Item>
                                        );

                                    default:
                                        return (
                                            <ContextMenu.Item
                                                className="context-menu-item"
                                                onSelect={() => onStartProduction && onStartProduction(assignment._productionRowId || assignment._originalId || assignment._id, 'start_cutting', separatedQuoteType)}
                                            >
                                                <Play size={14} className="mr-2" />
                                                Start Cutting
                                            </ContextMenu.Item>
                                        );
                                }
                            })()}
                        </>
                    ) : (
                        /* Assign Designers - Editor Page */
                        isSeparated ? (
                            /* SEPARATED VIEW: Direct designer assignment for the specific quote type */
                            hasQuoteType(separatedQuoteType) && (
                                <ContextMenu.Sub>
                                    <ContextMenu.SubTrigger className="context-menu-item context-menu-subtrigger">
                                        {getQuoteIcon(separatedQuoteType)}
                                        <span className="capitalize">Assign Designer</span>
                                        <div className="context-menu-arrow">▶</div>
                                    </ContextMenu.SubTrigger>
                                    <ContextMenu.Portal>
                                        <ContextMenu.SubContent className="context-menu-content context-menu-subcontent">
                                            {renderDirectDesignerList()}
                                        </ContextMenu.SubContent>
                                    </ContextMenu.Portal>
                                </ContextMenu.Sub>
                            )
                        ) : (
                            /* COMBINED VIEW: Traditional nested menu for all quote types */
                            (hasQuoteType('roof') || hasQuoteType('wall') || hasQuoteType('floor')) && (
                                <ContextMenu.Sub>
                                    <ContextMenu.SubTrigger className="context-menu-item context-menu-subtrigger">
                                        <Users size={14} className="mr-2" />
                                        Assign Designers
                                        <div className="context-menu-arrow">▶</div>
                                    </ContextMenu.SubTrigger>

                                    <ContextMenu.Portal>
                                        <ContextMenu.SubContent className="context-menu-content context-menu-subcontent">
                                            {hasQuoteType('roof') && renderDesignerSubmenu('roof', <Home size={14} className="mr-2" />)}
                                            {hasQuoteType('wall') && renderDesignerSubmenu('wall', <BrickWall size={14} className="mr-2" />)}
                                            {hasQuoteType('floor') && renderDesignerSubmenu('floor', <Grid2X2 size={14} className="mr-2" />)}
                                        </ContextMenu.SubContent>
                                    </ContextMenu.Portal>
                                </ContextMenu.Sub>
                            )
                        )
                    )}

                    {/* Undo Completion - Delivery page, completed deliveries, Admin Only */}
                    {canUndoDeliveryCompletion && (
                        <>
                            <ContextMenu.Separator className="context-menu-separator" />
                            <ContextMenu.Item
                                className="context-menu-item text-red-600"
                                onSelect={() => onUndoDeliveryCompletion(assignment, cardDeliveryRecord)}
                            >
                                <RotateCcw size={14} className="mr-2" />
                                Undo Completion
                            </ContextMenu.Item>
                        </>
                    )}

                    {/* Force Finish - Admin Only */}
                    {(page !== "department" && page !== "production") &&
                        <>
                            {hasPermission(userProfile?.role, ["app_owner", "super_admin", "admin"]) && (
                                <>
                                    <ContextMenu.Separator className="context-menu-separator" />
                                    <ContextMenu.Sub>
                                        <ContextMenu.SubTrigger className="context-menu-item context-menu-subtrigger text-orange-600">
                                            <Zap size={14} className="mr-2" />
                                            Force Finish
                                            <div className="context-menu-arrow">▶</div>
                                        </ContextMenu.SubTrigger>
                                        <ContextMenu.Portal>
                                            <ContextMenu.SubContent className="context-menu-content context-menu-subcontent">
                                                {/* Force Finish Design submenu */}
                                                {(hasQuoteType('roof') || hasQuoteType('wall') || hasQuoteType('floor')) && (
                                                    <ContextMenu.Sub>
                                                        <ContextMenu.SubTrigger className="context-menu-item context-menu-subtrigger">
                                                            <Home size={14} className="mr-2" />
                                                            Force Finish Design
                                                            <div className="context-menu-arrow">▶</div>
                                                        </ContextMenu.SubTrigger>
                                                        <ContextMenu.Portal>
                                                            <ContextMenu.SubContent className="context-menu-content context-menu-subcontent">
                                                                {hasQuoteType('roof') && !isDesignCompleted('roof') && (
                                                                    <ContextMenu.Item
                                                                        className="context-menu-item text-orange-600"
                                                                        onSelect={() => onForceFinish && onForceFinish(assignment._originalId || assignment._id, 'design', 'roof')}
                                                                    >
                                                                        <Home size={14} className="mr-2" />
                                                                        Roof
                                                                    </ContextMenu.Item>
                                                                )}
                                                                {hasQuoteType('wall') && !isDesignCompleted('wall') && (
                                                                    <ContextMenu.Item
                                                                        className="context-menu-item text-orange-600"
                                                                        onSelect={() => onForceFinish && onForceFinish(assignment._originalId || assignment._id, 'design', 'wall')}
                                                                    >
                                                                        <BrickWall size={14} className="mr-2" />
                                                                        Wall
                                                                    </ContextMenu.Item>
                                                                )}
                                                                {hasQuoteType('floor') && !isDesignCompleted('floor') && (
                                                                    <ContextMenu.Item
                                                                        className="context-menu-item text-orange-600"
                                                                        onSelect={() => onForceFinish && onForceFinish(assignment._originalId || assignment._id, 'design', 'floor')}
                                                                    >
                                                                        <Grid2X2 size={14} className="mr-2" />
                                                                        Floor
                                                                    </ContextMenu.Item>
                                                                )}
                                                                {(!hasQuoteType('roof') || isDesignCompleted('roof')) &&
                                                                    (!hasQuoteType('wall') || isDesignCompleted('wall')) &&
                                                                    (!hasQuoteType('floor') || isDesignCompleted('floor')) && (
                                                                        <ContextMenu.Item className="context-menu-item" disabled>
                                                                            <CheckCircle size={14} className="mr-2 text-green-500" />
                                                                            All designs completed
                                                                        </ContextMenu.Item>
                                                                    )}
                                                            </ContextMenu.SubContent>
                                                        </ContextMenu.Portal>
                                                    </ContextMenu.Sub>
                                                )}

                                                {/* Force Finish Estimation - Only for Estimation or Design & Estimation jobs */}
                                                {(assignment.jobType === 'Estimation' || assignment.jobType === 'Design & Estimation') && (
                                                    <ContextMenu.Item
                                                        className="context-menu-item text-orange-600"
                                                        onSelect={() => onForceFinish && onForceFinish(assignment._originalId || assignment._id, 'estimation')}
                                                        disabled={assignment.estimator?.is_completed || assignment.estimator?.isCompleted}
                                                    >
                                                        <Layers size={14} className="mr-2" />
                                                        Force Finish Estimation
                                                        {(assignment.estimator?.is_completed || assignment.estimator?.isCompleted) && (
                                                            <CheckCircle size={14} className="ml-2 text-green-500" />
                                                        )}
                                                    </ContextMenu.Item>
                                                )}

                                                {/* Force Finish Estimation - Only for Estimation or Design & Estimation jobs */}
                                                <ContextMenu.Item
                                                    className="context-menu-item text-orange-600"
                                                    onSelect={() => { }}
                                                    disabled
                                                >
                                                    <Truck size={14} className="mr-2" />
                                                    Force Finish Production
                                                    {/* {(assignment.estimator?.is_completed || assignment.estimator?.isCompleted) && (
                                                    <CheckCircle size={14} className="ml-2 text-green-500" />
                                                )} */}
                                                </ContextMenu.Item>


                                                {/* Force Finish Estimation - Only for Estimation or Design & Estimation jobs */}
                                                {(assignment.jobType !== 'Estimation' && !assignment?._isAccordionParent) && (
                                                    <ContextMenu.Item
                                                        className={`context-menu-item text-orange-600 ${(deliveryAlreadyFinished || !canForceFinishDelivery) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                        disabled={deliveryAlreadyFinished || !canForceFinishDelivery}
                                                        onSelect={() => {
                                                            if (deliveryAlreadyFinished || !canForceFinishDelivery) return;

                                                            onForceFinish && onForceFinish(
                                                                assignment._originalId || assignment._id || assignment.id,
                                                                deliveryForceFinishPayload,
                                                                'delivery'
                                                            );
                                                        }}
                                                    >
                                                        <Truck size={14} className="mr-2" />
                                                        {!canForceFinishDelivery
                                                            ? 'Force Finish Delivery (open quote row)'
                                                            : deliveryAlreadyFinished
                                                                ? 'Already Delivered'
                                                                : 'Force Finish Delivery'}
                                                    </ContextMenu.Item>
                                                )}

                                                <ContextMenu.Separator className="context-menu-separator" />

                                                {/* Force Finish All */}
                                                <ContextMenu.Item
                                                    className="context-menu-item text-red-600 font-semibold"
                                                    onSelect={() => onForceFinish && onForceFinish(assignment._originalId || assignment._id, 'all')}
                                                >
                                                    <Zap size={14} className="mr-2" />
                                                    Force Finish All
                                                </ContextMenu.Item>
                                            </ContextMenu.SubContent>
                                        </ContextMenu.Portal>
                                    </ContextMenu.Sub>
                                </>
                            )}
                        </>
                    }
                </ContextMenu.Content>
            </ContextMenu.Portal>

            {/* Duplicate Job Modal */}
            {isDuplicateModalOpen && (
                <div className="mobile-dialog-shell fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-96">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Duplicate Job</h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Duplicating: <span className="font-semibold">{assignment.uniqueId || assignment.unique_id}</span>
                        </p>

                        <div className="mb-4">
                            <label htmlFor="newJobId" className="block text-sm font-medium text-gray-700 mb-2">
                                New Job ID <span className="text-red-500">*</span>
                            </label>
                            <input
                                id="newJobId"
                                type="text"
                                value={newJobId}
                                onChange={(e) => {
                                    setNewJobId(e.target.value);
                                    setDuplicateError('');
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter new job ID"
                                disabled={isDuplicating}
                            />
                            {duplicateError && (
                                <p className="mt-2 text-sm text-red-600">{duplicateError}</p>
                            )}
                        </div>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setIsDuplicateModalOpen(false);
                                    setNewJobId('');
                                    setDuplicateError('');
                                }}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                                disabled={isDuplicating}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDuplicateSubmit}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={isDuplicating}
                            >
                                {isDuplicating ? 'Duplicating...' : 'Submit'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </ContextMenu.Root>
    );
};

export default AssignmentContextMenu;